// shared/api/BaseService.ts
import { ApiError } from "./ApiError";
import { getApiResponseStatus, type ApiClient } from "./ApiClient";
import type { FinalResponse } from "../models/api-gateway/gateway";
import { unwrapSingleService } from "../models/api-gateway/utility";

interface UnwrapErrorMessages {
  missing?: string;
  failed?: (status: number) => string;
}

export abstract class BaseService {
  protected readonly api: ApiClient;

  constructor(api: ApiClient) {
    this.api = api;
  }

  protected unwrap<T>(
    res: FinalResponse<T>,
    serviceName: string,
    messages: UnwrapErrorMessages = {},
  ): T {
    const outerStatus = getApiResponseStatus(res);
    const { found, status, value } = unwrapSingleService(res, serviceName);
    if (!found) {
      throw new ApiError(
        messages.missing ??
          `Failed to unwrap service response for "${serviceName}".`,
        {
          kind: "protocol",
          outerStatus,
          serviceName,
          payload: res,
        },
      );
    }
    if (status === null) {
      throw new ApiError(
        `Service "${serviceName}" returned an invalid response.`,
        {
          kind: "protocol",
          outerStatus,
          serviceName,
          payload: res,
        },
      );
    }
    if (status < 200 || status >= 300) {
      throw new ApiError(
        messages.failed?.(status) ??
          `Service "${serviceName}" failed with status ${status}.`,
        {
          kind: "service",
          outerStatus,
          serviceStatus: status,
          serviceName,
          payload: value,
        },
      );
    }
    return value as T;
  }

  protected unwrapDetail<T>(res: FinalResponse<T>, serviceName: string): T {
    const value = this.unwrap(res, serviceName);
    if (value == null) {
      const { status } = unwrapSingleService(res, serviceName);
      throw new ApiError(
        `Service "${serviceName}" returned an empty resource.`,
        {
          kind: "empty-resource",
          outerStatus: getApiResponseStatus(res),
          serviceStatus: status ?? undefined,
          serviceName,
          payload: value,
        },
      );
    }
    return value;
  }
}
